import { Button, Flex, Text } from '@/components';
import {
  type ProjectTableColumn,
  type ProjectTableColumnCategory,
  ProjectTableColumnCategoryLabelModifier,
} from '@/features/project/_models/project-filter';
import { DISPLAY_SETTING_BODY_HEIGHT } from '@/features/project/_utils/constants';
import { ArrowDownOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { type FC, Fragment, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export type ColumnCategories = {
  categoryKey: ProjectTableColumnCategory;
  columnKeys: ProjectTableColumn[];
}[];

type Props = {
  columns: {
    title: string;
    key: string;
  }[];
  columnCategories?: ColumnCategories;
  onShow: (columnKey: string) => void;
};

export const DisplaySettingHiddenColumns: FC<Props> = ({
  columns,
  columnCategories,
  onShow,
}) => {
  const { t } = useTranslation();
  const categoryDivRefs = useRef<
    Partial<Record<ProjectTableColumnCategory, HTMLDivElement>>
  >({});

  const columnCategoryRefCallbackFunctions = useMemo(
    () =>
      columnCategories?.map((columnCategory) => ({
        categoryKey: columnCategory.categoryKey,
        refCallbackFunction: (node: HTMLDivElement | null) => {
          categoryDivRefs.current[columnCategory.categoryKey] =
            node ?? undefined;
        },
      })),
    [columnCategories],
  );

  const categoryColumnKeysForButtons: ProjectTableColumnCategory[][] = [
    ['basicInformation'],
    ['revenueLatest', 'expenditureLatest'],
    ['revenueRequested', 'expenditureRequested'],
    ['revenueAssessed', 'expenditureAssessed'],
    ['revenueNotified', 'expenditureNotified'],
    ['discontinuedAmount'],
  ];

  const categoryStartColumnIndices = columnCategories?.map(
    (columnCategory) => ({
      categoryKey: columnCategory.categoryKey,
      startColumnIndex: columns.findIndex((column) =>
        columnCategory.columnKeys.some(
          (categoryColumnKey) => categoryColumnKey === column.key,
        ),
      ),
    }),
  );

  const scrollToCategory = (categoryKey: ProjectTableColumnCategory) => {
    const categoryDivRef = categoryDivRefs.current[categoryKey];
    categoryDivRef?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <Flex gap={16} vertical>
      <Text
        variant="footnote"
        color="secondary"
        text={t('search-setting-hidden')}
      />
      <Flex
        gap={4}
        vertical
        style={{
          maxHeight: DISPLAY_SETTING_BODY_HEIGHT,
          overflowY: 'auto',
        }}
      >
        {columnCategories &&
          categoryColumnKeysForButtons.map((categoryColumnKeys, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
            <Flex key={index} gap={8}>
              {categoryColumnKeys.map((categoryColumnKey) => (
                <Button
                  key={categoryColumnKey}
                  type="link"
                  onClick={() => scrollToCategory(categoryColumnKey)}
                  style={{ padding: 0 }}
                >
                  {ProjectTableColumnCategoryLabelModifier[categoryColumnKey]}
                  <ArrowDownOutlined />
                </Button>
              ))}
            </Flex>
          ))}
        {columns.map((column, index) => {
          const categoryKey = categoryStartColumnIndices?.find(
            (categoryStartColumnIndex) =>
              categoryStartColumnIndex.startColumnIndex === index,
          )?.categoryKey;
          return (
            <Fragment key={column.key}>
              {categoryKey && (
                <div
                  ref={
                    columnCategoryRefCallbackFunctions?.find(
                      (item) => item.categoryKey === categoryKey,
                    )?.refCallbackFunction
                  }
                  style={{ marginTop: 8, marginBottom: 8 }}
                >
                  <Text
                    weight="bold"
                    text={ProjectTableColumnCategoryLabelModifier[categoryKey]}
                  />
                </div>
              )}
              <Flex
                wrap="wrap"
                align="center"
                justify="space-between"
                style={{
                  marginLeft: categoryStartColumnIndices ? 8 : 0,
                  marginRight: 8,
                }}
              >
                <Text text={column.title} />
                <Button
                  type="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShow(column.key);
                  }}
                  icon={<EyeInvisibleOutlined style={{ fontSize: 16 }} />}
                />
              </Flex>
            </Fragment>
          );
        })}
      </Flex>
    </Flex>
  );
};
